import {
  Autocomplete,
  Divider,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "../../../../theme";
import SectionCard from "../../../reusableComponents/sectionCard/sectionCard";
import StatTile from "../../../reusableComponents/statTile/statTile";
import SegmentedControl from "../../../reusableComponents/segmentedControl/segmentedControl";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { LineChart, PieChart } from "@mui/x-charts";
import { styled } from "@mui/material/styles";
import { useAppSelector } from "../../../../hooks/hooks";
import {
  ConvertToObjectList,
  GetArray,
  GetObject,
  WithCommas,
} from "../../../../utils/utils";
import {
  BarChart,
  Bar,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
  TooltipProps,
} from "recharts";
import MiniSpinner from "../../../reusableComponents/miniSpinner/miniSpinner";
import {
  ValueType,
  NameType,
} from "recharts/types/component/DefaultTooltipContent";
import { markElementClasses } from "@mui/x-charts/LineChart";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const StyledText = styled("text")(({ theme }) => ({
  fill: tokens.chart.series.transaction,
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 10,
  fontWeight: "bold",
}));

function PieCenterLabel({ children }: { children: React.ReactNode }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    return (
      <Grid
        style={{
          border: "1px solid",
          backgroundColor: tokens.chart.tooltipBg,
          padding: "10px",
          borderRadius: "10px",
        }}
      >
        <Typography>{label}</Typography>
        <Divider />
        {payload.map((element: any, index: number) => (
          <Typography key={index} sx={{ color: element.color }}>{`${
            element?.name?.[0]?.toUpperCase() + element?.name?.slice(1)
          } : ${WithCommas(element.value)}`}</Typography>
        ))}
      </Grid>
    );
  }
  return null;
};

const CustomTooltipPercentage = ({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    return (
      <Grid
        style={{
          border: "1px solid",
          backgroundColor: tokens.chart.tooltipBg,
          padding: "10px",
          borderRadius: "10px",
        }}
      >
        <Typography>{label}</Typography>
        <Divider />
        {payload.map((element: any, index: number) => (
          <Typography key={index} sx={{ color: element.color }}>{`${
            element?.name?.[0]?.toUpperCase() + element?.name?.slice(1)
          } : ${WithCommas(element.value)} %`}</Typography>
        ))}
      </Grid>
    );
  }
  return null;
};

const CustomTooltipRevenue = ({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    return (
      <Grid
        style={{
          border: "1px solid",
          backgroundColor: tokens.chart.tooltipBg,
          padding: "10px",
          borderRadius: "10px",
        }}
      >
        <Typography>{label}</Typography>
        <Divider />
        {payload.map((element: any, index: number) => (
          <Typography key={index} sx={{ color: element.color }}>{`${
            element?.name?.[0]?.toUpperCase() + element?.name?.slice(1)
          } : $ ${WithCommas(element.value)}`}</Typography>
        ))}
      </Grid>
    );
  }
  return null;
};

const keyToLabel: { [key: string]: string } = {
  ACCOUNT_GROWTH: "Active Account",
  INVOICE_GROWTH: "Transaction",
  REVENUE_GROWTH: "Revenue",
  zero_growth: "Zero Growth",
};

const keyToLabel36: { [key: string]: string } = {
  ALL_ACCOUNT_GROWTH: "Active Account",
  ALL_INVOICE_GROWTH: "Transaction",
  ALL_REVENUE_GROWTH: "Revenue",
  zero_growth: "Zero Growth",
};

// Chart ink comes from the theme: legacy's config carries the original
// literals verbatim, so these render pixel-identical under legacy and follow
// the config under any other theme (2026-08-06 tokenization pilot).
const colors: { [key: string]: string } = {
  ACCOUNT_GROWTH: tokens.chart.series.activeAccount,
  INVOICE_GROWTH: tokens.chart.series.transaction,
  REVENUE_GROWTH: tokens.chart.series.revenue,
  zero_growth: tokens.chart.zeroLine,
  ALL_ACCOUNT_GROWTH: tokens.chart.series.activeAccount,
  ALL_INVOICE_GROWTH: tokens.chart.series.transaction,
  ALL_REVENUE_GROWTH: tokens.chart.series.revenue,
};

/**
 * The forecast growth field holds a number, "" while it loads, or the sentence
 * "Not Enough Historical Data" when the client has none. Number() on that
 * sentence is NaN, which reached the screen as "NaN%".
 */
const isNumeric = (value: any) =>
  value !== "" && value !== null && value !== undefined && Number.isFinite(Number(value));

const GrowthForecast = () => {
  const {
    forecastData,
    revinvData,
    selectedBranch,
    selectedDepartment,
    atRiskData,
    benchRawData,
    dealerRawData,
    benchWSGRawData,
    benchBranches,
    wsr12RawData,
    surveyRawData,
    atRiskLoading,
    forecastLoading,
    customerSurveyLoading,
    retentionRateLoading,
    gainLost6RawData,
    gainLost12RawData,
    gainLost36RawData,
    wsr36RawData,
    allRevinvData,
    getGrowthRateDetailLoading,
    emailReportsLoading,
  } = useAppSelector((state: any) => state.portalManagement);

  const [growthForecastAccountGrowth, setGrowthForecastAccountGrowth] =
    useState("");
  const [growthForecastRevenueGrowth, setGrowthForecastRevenueGrowth] =
    useState("");
  const [growthForecastInvoiceGrowth, setGrowthForecastInvoiceGrowth] =
    useState("");
  const [growthForecastActiveAccount, setGrowthForecastActiveAccount] =
    useState("");
  const [growthForecastActiveRevenue, setGrowthForecastActiveRevenue] =
    useState("");
  const [growthForecastActiveInvoice, setGrowthForecastActiveInvoice] =
    useState("");

  const [
    growthForecastChartGrowthRateData,
    setGrowthForecastChartGrowthRateData,
  ] = useState<any[]>([]);

  const [
    growthForecastChartGrowthRateData36,
    setGrowthForecastChartGrowthRateData36,
  ] = useState<any[]>([]);

  const [dealerBenchChartData, setDealerBenchChartData] = useState<any[]>([]);

  const [atRiskCustomerFrequency, setAtRiskCustomerFrequency] = useState<any>({
    chartData: [],
    risk_1: "",
    risk_2: "",
    stable: "",
  });
  const [atRiskRevenueFrequency, setAtRiskRevenueFrequency] = useState<any>({
    chartData: [],
    risk_1: "",
    risk_2: "",
    stable: "",
  });
  const [atRiskTransactionFrequency, setAtRiskTransactionFrequency] =
    useState<any>({
      chartData: [],
      risk_1: "",
      risk_2: "",
      stable: "",
    });

  const [growthForecastSurveyChartData, setGrowthForecastSurveyChartData] =
    useState<any[]>([]);

  const [growthForecastSurveyNegPercent, setGrowthForecastSurveyNegPercent] =
    useState("");
  const [growthForecastSurveyNeuPercent, setGrowthForecastSurveyNeuPercent] =
    useState("");
  const [growthForecastSurveyPosPercent, setGrowthForecastSurveyPosPercent] =
    useState("");
  const [growthForecastSurveyCurrentDate, setGrowthForecastSurveyCurrentDate] =
    useState("");

  const [growthForecastNetPromoterData, setGrowthForecastNetPromoterData] =
    useState<any[]>([]);

  const [growthForecastNetPromoter12m, setGrowthForecastNetPromoter12m] =
    useState("");
  const [growthForecastNetPromoter6m, setGrowthForecastNetPromoter6m] =
    useState("");
  const [growthForecastNetPromoter3m, setGrowthForecastNetPromoter3m] =
    useState("");

  const [growthForecastRetentionData, setGrowthForecastRetentionData] =
    useState<any[]>([]);

  const [growthForecastActiveAccountData, setGrowthForecastActiveAccountData] =
    useState<any[]>([]);

  const [growthForecastForecastChartData, setGrowthForecastForecastChartData] =
    useState<any[]>([]);

  const [growthForecastForecastActual, setGrowthForecastForecastActual] =
    useState("");
  const [growthForecastForecastForecast, setGrowthForecastForecastForecast] =
    useState("");
  const [growthForecastForecastGrowth, setGrowthForecastForecastGrowth] =
    useState("");

  const [growthForecastRevPerCustStable, setGrowthForecastRevPerCustStable] =
    useState<any>({});
  const [growthForecastRevPerCustAtrist1, setGrowthForecastRevPerCustAtrist1] =
    useState<any>({});
  const [growthForecastRevPerCustAtrist2, setGrowthForecastRevPerCustAtrist2] =
    useState<any>({});

  const [
    growthForecastForecastActualGrowth,
    setGrowthForecastForecastActualGrowth,
  ] = useState("");

  const [growthForecastLastRetentionRate, setGrowthForecastLastRetentionRate] =
    useState("");

  const [activeForecast, setActiveForecast] = useState("ORDER_FREQUENCY");
  const [activeGrowthRate, setActiveGrowthRate] = useState("36");
  const [activeNetPromoter, setActiveNetPromoter] = useState("SURVEY_RESULT");

  const [activeRetentionGainLost, setActiveRetentionGainLost] =
    useState("RETENTION");
  const [activeRetentionGainLostTitle, setActiveRetentionGainLostTitle] =
    useState("Retention Rate");

  const handleForecastNav = (value: string) => {
    setActiveForecast(value);
  };
  const handleGrowthRateNav = (value: string) => {
    // Re-selecting the active period must be a no-op. The clears below exist
    // so the rebuild effect (keyed on activeGrowthRate) starts from empty —
    // but when the value does not change, that effect never re-fires, so the
    // clears left the chart with no dataset and MUI X threw into the
    // ErrorBoundary.
    if (value === activeGrowthRate) return;
    setGrowthForecastChartGrowthRateData([]);
    setGrowthForecastChartGrowthRateData36([]);
    setActiveGrowthRate(value);
  };
  const handleNetPromoterNav = (value: string) => {
    setActiveNetPromoter(value);
  };
  const handleRetentionGainLost = (value: string, title: string) => {
    setActiveRetentionGainLost(value);
    setActiveRetentionGainLostTitle(title);
  };

  const [monthlyView, setMonthlyView] = useState(false);

  const [
    salesMarketingGainLostChartGainAcc,
    setSalesMarketingGainLostChartGainAcc,
  ] = useState([]);
  const [
    salesMarketingGainLostChartLostAcc,
    setSalesMarketingGainLostChartLostAcc,
  ] = useState([]);

  const [
    salesMarketingActiveAccountChartLabel,
    setsalesMarketingActiveAccountChartLabel,
  ] = useState([]);

  const [
    salesMarketingGainLost6ChartGainAcc,
    setsalesMarketingGainLost6ChartGainAcc,
  ] = useState([]);
  const [
    salesMarketingGainLost6ChartLostAcc,
    setsalesMarketingGainLost6ChartLostAcc,
  ] = useState([]);
  const [
    salesMarketingGainLost12ChartGainAcc,
    setsalesMarketingGainLost12ChartGainAcc,
  ] = useState([]);
  const [
    salesMarketingGainLost12ChartLostAcc,
    setsalesMarketingGainLost12ChartLostAcc,
  ] = useState([]);
  const [
    salesMarketingGainLost36ChartGainAcc,
    setsalesMarketingGainLost36ChartGainAcc,
  ] = useState([]);
  const [
    salesMarketingGainLost36ChartLostAcc,
    setsalesMarketingGainLost36ChartLostAcc,
  ] = useState([]);

  const [
    growthForecastDealerBenchmarkOption,
    setGrowthForecastDealerBenchmarkOption,
  ] = useState<any[]>([]);

  const [selectedDealerBenchmarkOption, setselectedDealerBenchmarkOption] =
    useState<any>({
      label: "all",
      value: "all",
    });

  const handleDealerBenchmarkChange = (selection: any) => {
    if (selection) {
      setselectedDealerBenchmarkOption(selection);
    } else
      setselectedDealerBenchmarkOption({
        label: "all",
        value: "all",
      });
  };

  const growthForecastFilterData = () => {
    if (revinvData || forecastData || allRevinvData) {
      const revInvDataFilter = GetObject(
        GetArray(revinvData).filter((item: any) => {
          return (
            item.BRANCH === selectedBranch.value &&
            item.PRODUCT_TYPE_ALL === selectedDepartment.value
          );
        })
      );
      const allRevInvDataFilter = GetObject(
        GetArray(allRevinvData).filter((item: any) => {
          return (
            item.ALL_BRANCH === selectedBranch.value &&
            item.ALL_PRODUCT_TYPE_ALL === selectedDepartment.value
          );
        })
      );
      const forecastDataFilter = GetObject(
        GetArray(forecastData).filter(
          (item: any) =>
            item.BRANCH === selectedBranch.value &&
            item.PRODUCT_TYPE_ALL === selectedDepartment.value
        )
      );

      if (
        Object.keys(revInvDataFilter)?.length > 0 ||
        Object.keys(forecastDataFilter)?.length > 0 ||
        Object.keys(allRevInvDataFilter)?.length > 0
      ) {
        if (
          revInvDataFilter?.ACCOUNT_GROWTH &&
          revInvDataFilter?.ACCOUNT_GROWTH?.length ===
            revInvDataFilter?.REVENUE_GROWTH?.length &&
          revInvDataFilter?.ACCOUNT_GROWTH?.length ===
            revInvDataFilter?.INVOICE_GROWTH?.length &&
          revInvDataFilter?.ACCOUNT_GROWTH?.length ===
            revInvDataFilter?.TOTAL_ACCOUNT?.length &&
          revInvDataFilter?.ACCOUNT_GROWTH?.length ===
            revInvDataFilter?.TOTAL_REVENUE?.length &&
          revInvDataFilter?.ACCOUNT_GROWTH?.length ===
            revInvDataFilter?.INVOICE_COUNT?.length
        ) {
          const lastIndex = revInvDataFilter?.ACCOUNT_GROWTH?.length - 1;
          setGrowthForecastAccountGrowth(
            revInvDataFilter?.ACCOUNT_GROWTH &&
              revInvDataFilter?.ACCOUNT_GROWTH?.length >= lastIndex
              ? revInvDataFilter?.ACCOUNT_GROWTH?.[lastIndex]
              : []
          );
          setGrowthForecastRevenueGrowth(
            revInvDataFilter?.REVENUE_GROWTH &&
              revInvDataFilter?.REVENUE_GROWTH?.length >= lastIndex
              ? revInvDataFilter?.REVENUE_GROWTH?.[lastIndex]
              : []
          );
          setGrowthForecastInvoiceGrowth(
            revInvDataFilter?.INVOICE_GROWTH &&
              revInvDataFilter?.INVOICE_GROWTH?.length >= lastIndex
              ? revInvDataFilter?.INVOICE_GROWTH?.[lastIndex]
              : []
          );
          setGrowthForecastActiveAccount(
            WithCommas(
              revInvDataFilter?.TOTAL_ACCOUNT &&
                revInvDataFilter?.TOTAL_ACCOUNT?.length >= lastIndex
                ? revInvDataFilter?.TOTAL_ACCOUNT?.[lastIndex]
                : ""
            )
          );
          setGrowthForecastActiveRevenue(
            WithCommas(
              revInvDataFilter?.TOTAL_REVENUE &&
                revInvDataFilter?.TOTAL_REVENUE?.length >= lastIndex
                ? revInvDataFilter?.TOTAL_REVENUE?.[lastIndex]
                : ""
            )
          );
          setGrowthForecastActiveInvoice(
            WithCommas(
              revInvDataFilter?.INVOICE_COUNT &&
                revInvDataFilter?.INVOICE_COUNT?.length >= lastIndex
                ? revInvDataFilter?.INVOICE_COUNT?.[lastIndex]
                : ""
            )
          );

          setGrowthForecastLastRetentionRate(
            revInvDataFilter.retention_rate &&
              revInvDataFilter?.retention_rate?.length >= lastIndex
              ? revInvDataFilter.retention_rate[lastIndex]
              : "-"
          );
        } else {
          setGrowthForecastAccountGrowth("");
          setGrowthForecastRevenueGrowth("");
          setGrowthForecastInvoiceGrowth("");
          setGrowthForecastActiveAccount("");
          setGrowthForecastActiveRevenue("");
          setGrowthForecastActiveInvoice("");
          setGrowthForecastForecastActualGrowth("Not Enough Historical Data");
          setGrowthForecastLastRetentionRate("-");
        }

        setGrowthForecastForecastActualGrowth(
          activeForecast === "ORDER_FREQUENCY"
            ? revInvDataFilter?.INVOICE_GROWTH
              ? revInvDataFilter?.INVOICE_GROWTH?.[
                  revInvDataFilter?.INVOICE_GROWTH?.length - 1
                ]
              : ""
            : activeForecast === "ACTIVE_ACCOUNT"
            ? revInvDataFilter?.ACCOUNT_GROWTH
              ? revInvDataFilter?.ACCOUNT_GROWTH?.[
                  revInvDataFilter?.ACCOUNT_GROWTH?.length - 1
                ]
              : ""
            : revInvDataFilter?.REVENUE_GROWTH
            ? revInvDataFilter?.REVENUE_GROWTH?.[
                revInvDataFilter?.REVENUE_GROWTH?.length - 1
              ]
            : ""
        );

        if (
          revInvDataFilter?.END_PERIOD &&
          revInvDataFilter?.END_PERIOD?.length > 0
        ) {
          let tempGrowthRateData: any[] = [];
          revInvDataFilter?.END_PERIOD?.forEach((period: any, index: any) => {
            tempGrowthRateData.push({
              period: period,
              ACCOUNT_GROWTH: revInvDataFilter?.ACCOUNT_GROWTH
                ? revInvDataFilter?.ACCOUNT_GROWTH?.[index]
                : "",
              INVOICE_GROWTH: revInvDataFilter?.INVOICE_GROWTH
                ? revInvDataFilter?.INVOICE_GROWTH?.[index]
                : "",
              REVENUE_GROWTH: revInvDataFilter?.REVENUE_GROWTH
                ? revInvDataFilter?.REVENUE_GROWTH?.[index]
                : "",
              INVOICE_COUNT: revInvDataFilter?.INVOICE_COUNT
                ? revInvDataFilter?.INVOICE_COUNT?.[index]
                : "",
              TOTAL_ACCOUNT: revInvDataFilter?.TOTAL_ACCOUNT
                ? revInvDataFilter?.TOTAL_ACCOUNT?.[index]
                : "",
              TOTAL_REVENUE: revInvDataFilter?.TOTAL_REVENUE
                ? revInvDataFilter?.TOTAL_REVENUE?.[index]
                : "",
              zero_growth: 0,
            });
          });
          setGrowthForecastChartGrowthRateData(tempGrowthRateData);
        } else {
          setGrowthForecastChartGrowthRateData([]);
        }

        if (
          allRevInvDataFilter?.ALL_END_PERIOD &&
          allRevInvDataFilter?.ALL_END_PERIOD?.length > 0
        ) {
          let tempGrowthRateData36: any[] = [];
          allRevInvDataFilter?.ALL_END_PERIOD?.forEach(
            (period: any, index: any) => {
              tempGrowthRateData36.push({
                period: period,
                ALL_ACCOUNT_GROWTH: allRevInvDataFilter?.ALL_ACCOUNT_GROWTH
                  ? allRevInvDataFilter?.ALL_ACCOUNT_GROWTH?.[index]
                  : "",
                ALL_INVOICE_GROWTH: allRevInvDataFilter?.ALL_INVOICE_GROWTH
                  ? allRevInvDataFilter?.ALL_INVOICE_GROWTH?.[index]
                  : "",
                ALL_REVENUE_GROWTH: allRevInvDataFilter?.ALL_REVENUE_GROWTH
                  ? allRevInvDataFilter?.ALL_REVENUE_GROWTH?.[index]
                  : "",
                ALL_INVOICE_COUNT: allRevInvDataFilter?.ALL_INVOICE_COUNT
                  ? allRevInvDataFilter?.ALL_INVOICE_COUNT?.[index]
                  : "",
                ALL_TOTAL_ACCOUNT: allRevInvDataFilter?.ALL_TOTAL_ACCOUNT
                  ? allRevInvDataFilter?.ALL_TOTAL_ACCOUNT?.[index]
                  : "",
                ALL_TOTAL_REVENUE: allRevInvDataFilter?.ALL_TOTAL_REVENUE
                  ? allRevInvDataFilter?.ALL_TOTAL_REVENUE?.[index]
                  : "",
                zero_growth: 0,
              });
            }
          );

          setGrowthForecastChartGrowthRateData36(tempGrowthRateData36);
        } else {
          setGrowthForecastChartGrowthRateData36([]);
        }

        const tempGrowthForecastRetentionData: any[] = [];
        const tempGrowthForecastActiveAccountData: any[] = [];
        if (
          revInvDataFilter?.END_PERIOD &&
          revInvDataFilter?.END_PERIOD?.length ===
            revInvDataFilter?.retention_rate?.length &&
          revInvDataFilter?.END_PERIOD?.length ===
            revInvDataFilter?.ret_target?.length
        ) {
          revInvDataFilter?.END_PERIOD.forEach((element: any, index: any) => {
            tempGrowthForecastRetentionData.push({
              name: element,
              "Retention Rate": revInvDataFilter?.retention_rate?.[index]
                ? revInvDataFilter?.retention_rate?.[index]
                : "",
              "Retention Goal": revInvDataFilter?.ret_target?.[index]
                ? revInvDataFilter?.ret_target?.[index]
                : "",
            });
            tempGrowthForecastActiveAccountData.push({
              name: element,
              "Account %": revInvDataFilter?.ACCOUNT_GROWTH?.[index]
                ? revInvDataFilter?.ACCOUNT_GROWTH?.[index]
                : "",
              "Active Account": revInvDataFilter?.TOTAL_ACCOUNT?.[index]
                ? revInvDataFilter?.TOTAL_ACCOUNT?.[index]
                : "",
            });
          });
        }

        setGrowthForecastRetentionData(tempGrowthForecastRetentionData);
        setGrowthForecastActiveAccountData(tempGrowthForecastActiveAccountData);

        const tempGrowthForecastForecastChartData: any[] = [];

        if (forecastDataFilter?.forecast_date) {
          forecastDataFilter?.forecast_date.forEach(
            (element: any, index: any) => {
              tempGrowthForecastForecastChartData.push({
                name: element,
                lower:
                  activeForecast === "ORDER_FREQUENCY"
                    ? monthlyView
                      ? forecastDataFilter?.forecast_l_freq
                        ? forecastDataFilter?.forecast_l_freq?.[index]
                        : 0
                      : forecastDataFilter?.forecast_12_l_freq
                      ? forecastDataFilter?.forecast_12_l_freq?.[index]
                      : 0
                    : activeForecast === "ACTIVE_ACCOUNT"
                    ? monthlyView
                      ? forecastDataFilter?.forecast_l_acc
                        ? forecastDataFilter?.forecast_l_acc?.[index]
                        : 0
                      : forecastDataFilter?.forecast_12_l_acc
                      ? forecastDataFilter?.forecast_12_l_acc?.[index]
                      : 0
                    : monthlyView
                    ? forecastDataFilter?.forecast_l_rev
                      ? forecastDataFilter?.forecast_l_rev?.[index]
                      : 0
                    : forecastDataFilter?.forecast_12_l_rev
                    ? forecastDataFilter?.forecast_12_l_rev?.[index]
                    : 0,
                actual:
                  activeForecast === "ORDER_FREQUENCY"
                    ? monthlyView
                      ? forecastDataFilter?.actual_frequency
                        ? forecastDataFilter?.actual_frequency?.[index]
                        : 0
                      : forecastDataFilter?.actual_12_freq
                      ? forecastDataFilter?.actual_12_freq?.[index]
                      : 0
                    : activeForecast === "ACTIVE_ACCOUNT"
                    ? monthlyView
                      ? forecastDataFilter?.actual_account
                        ? forecastDataFilter?.actual_account?.[index]
                        : 0
                      : forecastDataFilter?.actual_12_acc
                      ? forecastDataFilter?.actual_12_acc?.[index]
                      : 0
                    : monthlyView
                    ? forecastDataFilter?.actual_revenue
                      ? forecastDataFilter?.actual_revenue?.[index]
                      : 0
                    : forecastDataFilter?.actual_12_rev
                    ? forecastDataFilter?.actual_12_rev?.[index]
                    : 0,
                upper:
                  activeForecast === "ORDER_FREQUENCY"
                    ? monthlyView
                      ? forecastDataFilter?.forecast_u_freq
                        ? forecastDataFilter?.forecast_u_freq?.[index]
                        : 0
                      : forecastDataFilter?.forecast_12_u_freq
                      ? forecastDataFilter?.forecast_12_u_freq?.[index]
                      : 0
                    : activeForecast === "ACTIVE_ACCOUNT"
                    ? monthlyView
                      ? forecastDataFilter?.forecast_u_acc
                        ? forecastDataFilter?.forecast_u_acc?.[index]
                        : 0
                      : forecastDataFilter?.forecast_12_u_acc
                      ? forecastDataFilter?.forecast_12_u_acc?.[index]
                      : 0
                    : monthlyView
                    ? forecastDataFilter?.forecast_u_rev
                      ? forecastDataFilter?.forecast_u_rev?.[index]
                      : 0
                    : forecastDataFilter?.forecast_12_u_rev
                    ? forecastDataFilter?.forecast_12_u_rev?.[index]
                    : 0,
                forecast:
                  activeForecast === "ORDER_FREQUENCY"
                    ? monthlyView
                      ? forecastDataFilter?.forecast_freq
                        ? forecastDataFilter?.forecast_freq?.[index]
                        : 0
                      : forecastDataFilter?.forecast_12_freq
                      ? forecastDataFilter?.forecast_12_freq?.[index]
                      : 0
                    : activeForecast === "ACTIVE_ACCOUNT"
                    ? monthlyView
                      ? forecastDataFilter?.forecast_acc
                        ? forecastDataFilter?.forecast_acc?.[index]
                        : 0
                      : forecastDataFilter?.forecast_12_acc
                      ? forecastDataFilter?.forecast_12_acc?.[index]
                      : 0
                    : monthlyView
                    ? forecastDataFilter?.forecast_rev
                      ? forecastDataFilter?.forecast_rev?.[index]
                      : 0
                    : forecastDataFilter?.forecast_12_rev
                    ? forecastDataFilter?.forecast_12_rev?.[index]
                    : 0,
              });
            }
          );
        }

        setGrowthForecastForecastChartData(tempGrowthForecastForecastChartData);

        setGrowthForecastForecastActual(
          forecastDataFilter?.actual_12_freq &&
            forecastDataFilter?.actual_12_freq?.length >= 11 &&
            activeForecast === "ORDER_FREQUENCY"
            ? WithCommas(
                forecastDataFilter?.actual_12_freq &&
                  forecastDataFilter?.actual_12_freq?.length >= 11
                  ? forecastDataFilter?.actual_12_freq?.[11]
                  : ""
              )
            : forecastDataFilter?.actual_12_acc &&
              forecastDataFilter?.actual_12_acc?.length >= 11 &&
              activeForecast === "ACTIVE_ACCOUNT"
            ? WithCommas(
                forecastDataFilter?.actual_12_acc &&
                  forecastDataFilter?.actual_12_acc?.length >= 11
                  ? forecastDataFilter?.actual_12_acc?.[11]
                  : ""
              )
            : forecastDataFilter?.actual_12_rev &&
              forecastDataFilter?.actual_12_rev?.length >= 11
            ? "$" +
              WithCommas(
                forecastDataFilter?.actual_12_rev &&
                  forecastDataFilter?.actual_12_rev?.length >= 11
                  ? forecastDataFilter?.actual_12_rev?.[11]
                  : ""
              )
            : ""
        );
        setGrowthForecastForecastForecast(
          forecastDataFilter?.forecast_12_freq &&
            activeForecast === "ORDER_FREQUENCY"
            ? WithCommas(
                forecastDataFilter?.forecast_12_freq &&
                  forecastDataFilter?.forecast_12_freq?.length >= 23
                  ? forecastDataFilter?.forecast_12_freq?.[23]
                  : ""
              )
            : forecastDataFilter?.forecast_12_acc &&
              activeForecast === "ACTIVE_ACCOUNT"
            ? WithCommas(
                forecastDataFilter?.forecast_12_acc &&
                  forecastDataFilter?.forecast_12_acc?.length >= 23
                  ? forecastDataFilter?.forecast_12_acc?.[23]
                  : ""
              )
            : forecastDataFilter?.forecast_12_rev
            ? "$" +
              WithCommas(
                forecastDataFilter?.forecast_12_rev &&
                  forecastDataFilter?.forecast_12_rev?.length >= 23
                  ? forecastDataFilter?.forecast_12_rev?.[23]
                  : ""
              )
            : ""
        );
        setGrowthForecastForecastGrowth(
          activeForecast === "ORDER_FREQUENCY"
            ? forecastDataFilter?.freq_growth &&
              forecastDataFilter?.freq_growth?.length >= 23
              ? forecastDataFilter?.freq_growth?.[23]
              : ""
            : activeForecast === "ACTIVE_ACCOUNT"
            ? forecastDataFilter?.acc_growth &&
              forecastDataFilter?.acc_growth?.length >= 23
              ? forecastDataFilter?.acc_growth?.[23]
              : ""
            : forecastDataFilter?.rev_growth &&
              forecastDataFilter?.rev_growth?.length >= 23
            ? forecastDataFilter?.rev_growth?.[23]
            : ""
        );

        setSalesMarketingGainLostChartGainAcc(
          revInvDataFilter?.GAINED_ACCOUNT
            ? revInvDataFilter?.GAINED_ACCOUNT
            : []
        );
        setSalesMarketingGainLostChartLostAcc(
          revInvDataFilter?.LOST_ACCOUNT ? revInvDataFilter?.LOST_ACCOUNT : []
        );
        setsalesMarketingActiveAccountChartLabel(
          revInvDataFilter?.END_PERIOD ? revInvDataFilter?.END_PERIOD : []
        );
      } else {
        setGrowthForecastChartGrowthRateData([]);

        setGrowthForecastForecastActual("");
        setGrowthForecastForecastForecast("");
        setGrowthForecastForecastGrowth("Not Enough Historical Data");

        setSalesMarketingGainLostChartGainAcc([]);
        setSalesMarketingGainLostChartLostAcc([]);
        setsalesMarketingActiveAccountChartLabel([]);
      }
    }
    if (benchRawData) {
      let completeOptions = benchRawData?.dealer_type
        ? ConvertToObjectList(benchRawData?.dealer_type)
        : [];

      setGrowthForecastDealerBenchmarkOption(
        completeOptions.filter((obj: any, index: any) => {
          return (
            index ===
            completeOptions.findIndex((o: any) => obj.value === o.value)
          );
        })
      );

      const salrankFilter = GetObject(
        GetArray(benchRawData).filter(
          (item: any) =>
            item.BRANCH === selectedBranch.value &&
            item.dealer_type === selectedDealerBenchmarkOption.value
        )
      );

      let tempDealerBenchChartData: any[] = [];
      let tempDuplicate: any[] = [];
      if (
        salrankFilter?.key_metric &&
        salrankFilter?.key_metric?.length ===
          salrankFilter.client_perf?.length &&
        salrankFilter?.key_metric?.length === salrankFilter.avg_per?.length
      ) {
        salrankFilter?.key_metric.forEach((element: any, index: any) => {
          if (!tempDuplicate.includes(element)) {
            tempDealerBenchChartData.push({
              name: element,
              you: salrankFilter.client_perf[index],
              average: salrankFilter.avg_per[index],
            });
            tempDuplicate.push(element);
          }
        });
      }

      setDealerBenchChartData(tempDealerBenchChartData);
    }
    if (atRiskData) {
      const atRiskFilter = GetObject(
        GetArray(atRiskData).filter(
          (item: any) =>
            item.BRANCH === selectedBranch.value &&
            item.PRODUCT_TYPE_ALL === selectedDepartment.value
        )
      );

      if (Object.keys(atRiskFilter)?.length > 0) {
        if (
          atRiskFilter.CUST_FREQ?.length >= 3 &&
          atRiskFilter?.CUST_COUNT?.length >= 3
        ) {
          const customerFrequency = [
            {
              label: `RISK 1: ${
                atRiskFilter?.CUST_FREQ && atRiskFilter?.CUST_FREQ?.length >= 1
                  ? atRiskFilter?.CUST_FREQ?.[0]
                  : ""
              }%`,
              value: Number(
                atRiskFilter?.CUST_COUNT &&
                  atRiskFilter?.CUST_COUNT?.length >= 1
                  ? atRiskFilter?.CUST_COUNT?.[0]
                  : 0
              ),
              color: tokens.chart.risk.risk1,
            },
            {
              label: `RISK 2: ${
                atRiskFilter?.CUST_FREQ && atRiskFilter?.CUST_FREQ?.length >= 2
                  ? atRiskFilter?.CUST_FREQ?.[1]
                  : ""
              }%`,
              value: Number(
                atRiskFilter?.CUST_COUNT &&
                  atRiskFilter?.CUST_COUNT?.length >= 2
                  ? atRiskFilter?.CUST_COUNT?.[1]
                  : 0
              ),
              color: tokens.chart.risk.risk2,
            },
            {
              label: `STABLE: ${
                atRiskFilter?.CUST_FREQ && atRiskFilter?.CUST_FREQ?.length >= 3
                  ? atRiskFilter?.CUST_FREQ?.[2]
                  : ""
              }%`,
              value: Number(
                atRiskFilter?.CUST_COUNT &&
                  atRiskFilter?.CUST_COUNT?.length >= 3
                  ? atRiskFilter?.CUST_COUNT?.[2]
                  : 0
              ),
              color: tokens.chart.risk.stable,
            },
          ];

          setAtRiskCustomerFrequency({
            chartData: customerFrequency,
            risk_1:
              atRiskFilter?.CUST_FREQ && atRiskFilter?.CUST_FREQ?.length >= 1
                ? atRiskFilter?.CUST_FREQ?.[0]
                : 0,
            risk_2:
              atRiskFilter?.CUST_FREQ && atRiskFilter?.CUST_FREQ?.length >= 2
                ? atRiskFilter?.CUST_FREQ?.[1]
                : 0,
            stable:
              atRiskFilter?.CUST_FREQ && atRiskFilter?.CUST_FREQ?.length >= 3
                ? atRiskFilter?.CUST_FREQ?.[2]
                : 0,
          });
        }

        if (
          atRiskFilter?.REVENUE_FREQ?.length >= 3 &&
          atRiskFilter?.REVENUE?.length >= 3
        ) {
          const revenueFrequency = [
            {
              label: `RISK 1:${
                atRiskFilter?.REVENUE_FREQ &&
                atRiskFilter?.REVENUE_FREQ?.length >= 1
                  ? atRiskFilter?.REVENUE_FREQ?.[0]
                  : ""
              } %`,
              value: Number(
                atRiskFilter?.REVENUE && atRiskFilter?.REVENUE?.length >= 1
                  ? atRiskFilter?.REVENUE?.[0]
                  : 0
              ),
              color: tokens.chart.risk.risk1,
            },
            {
              label: `RISK 2: ${
                atRiskFilter?.REVENUE_FREQ &&
                atRiskFilter?.REVENUE_FREQ?.length >= 2
                  ? atRiskFilter?.REVENUE_FREQ?.[1]
                  : 0
              } %`,
              value: Number(
                atRiskFilter?.REVENUE && atRiskFilter?.REVENUE?.length >= 2
                  ? atRiskFilter?.REVENUE?.[1]
                  : 0
              ),
              color: tokens.chart.risk.risk2,
            },
            {
              label: `STABLE: ${
                atRiskFilter?.REVENUE_FREQ &&
                atRiskFilter?.REVENUE_FREQ?.length >= 3
                  ? atRiskFilter?.REVENUE_FREQ?.[2]
                  : ""
              } %`,
              value:
                atRiskFilter?.REVENUE && atRiskFilter?.REVENUE?.length >= 3
                  ? atRiskFilter?.REVENUE?.[2]
                  : 0,
              color: tokens.chart.risk.stable,
            },
          ];

          setAtRiskRevenueFrequency({
            chartData: revenueFrequency,
            risk_1:
              atRiskFilter?.REVENUE_FREQ &&
              atRiskFilter?.REVENUE_FREQ?.length >= 1
                ? atRiskFilter?.REVENUE_FREQ?.[0]
                : 0,
            risk_2:
              atRiskFilter?.REVENUE_FREQ &&
              atRiskFilter?.REVENUE_FREQ?.length >= 2
                ? atRiskFilter?.REVENUE_FREQ?.[1]
                : 0,
            stable:
              atRiskFilter?.REVENUE_FREQ &&
              atRiskFilter?.REVENUE_FREQ?.length >= 3
                ? atRiskFilter?.REVENUE_FREQ?.[2]
                : 0,
          });
        }

        if (
          atRiskFilter?.ORDER_FREQ?.length >= 3 &&
          atRiskFilter?.ORDER_FREQUENCY?.length >= 3
        ) {
          const transactionFrequency = [
            {
              label: `RISK 1: ${
                atRiskFilter?.ORDER_FREQ &&
                atRiskFilter?.ORDER_FREQ?.length >= 1
                  ? atRiskFilter?.ORDER_FREQ?.[0]
                  : ""
              }%`,
              value: Number(
                atRiskFilter?.ORDER_FREQUENCY &&
                  atRiskFilter?.ORDER_FREQUENCY?.length >= 1
                  ? atRiskFilter?.ORDER_FREQUENCY?.[0]
                  : 0
              ),
              color: tokens.chart.risk.risk1,
            },
            {
              label: `RISK 2: ${
                atRiskFilter?.ORDER_FREQ &&
                atRiskFilter?.ORDER_FREQ?.length >= 2
                  ? atRiskFilter?.ORDER_FREQ?.[1]
                  : 0
              }%`,
              value: Number(
                atRiskFilter?.ORDER_FREQUENCY &&
                  atRiskFilter?.ORDER_FREQUENCY?.length >= 2
                  ? atRiskFilter?.ORDER_FREQUENCY?.[1]
                  : 0
              ),
              color: tokens.chart.risk.risk2,
            },
            {
              label: `STABLE: ${
                atRiskFilter?.ORDER_FREQ &&
                atRiskFilter?.ORDER_FREQ?.length >= 3
                  ? atRiskFilter?.ORDER_FREQ?.[2]
                  : ""
              }%`,
              value:
                atRiskFilter?.ORDER_FREQUENCY &&
                atRiskFilter?.ORDER_FREQUENCY?.length >= 3
                  ? atRiskFilter?.ORDER_FREQUENCY?.[2]
                  : 0,
              color: tokens.chart.risk.stable,
            },
          ];

          setAtRiskTransactionFrequency({
            chartData: transactionFrequency,
            risk_1:
              atRiskFilter?.ORDER_FREQ && atRiskFilter?.ORDER_FREQ?.length >= 1
                ? atRiskFilter?.ORDER_FREQ?.[0]
                : 0,
            risk_2:
              atRiskFilter?.ORDER_FREQ && atRiskFilter?.ORDER_FREQ?.length >= 2
                ? atRiskFilter?.ORDER_FREQ?.[1]
                : 0,
            stable:
              atRiskFilter?.ORDER_FREQ && atRiskFilter?.ORDER_FREQ?.length >= 3
                ? atRiskFilter?.ORDER_FREQ?.[2]
                : 0,
          });
        }
        if (atRiskFilter?.REV_PER_CUST?.length >= 3) {
          setGrowthForecastRevPerCustStable({
            REV_PER_CUST:
              atRiskFilter?.REV_PER_CUST &&
              atRiskFilter?.REV_PER_CUST?.length >= 3
                ? atRiskFilter?.REV_PER_CUST?.[2]
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : 0,
            CUST_COUNT:
              atRiskFilter?.CUST_COUNT &&
              atRiskFilter?.REV_PER_CUST?.length >= 3
                ? atRiskFilter?.CUST_COUNT?.[2]
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : 0,
          });
          setGrowthForecastRevPerCustAtrist1({
            REV_PER_CUST:
              atRiskFilter?.REV_PER_CUST &&
              atRiskFilter?.REV_PER_CUST?.length >= 1
                ? atRiskFilter?.REV_PER_CUST?.[0]
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : 0,
            CUST_COUNT:
              atRiskFilter?.CUST_COUNT &&
              atRiskFilter?.REV_PER_CUST?.length >= 3
                ? atRiskFilter?.CUST_COUNT?.[0]
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : 0,
          });
          setGrowthForecastRevPerCustAtrist2({
            REV_PER_CUST:
              atRiskFilter?.REV_PER_CUST &&
              atRiskFilter?.REV_PER_CUST?.length >= 2
                ? atRiskFilter?.REV_PER_CUST?.[1]
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : 0,
            CUST_COUNT:
              atRiskFilter?.CUST_COUNT &&
              atRiskFilter?.REV_PER_CUST?.length >= 3
                ? atRiskFilter?.CUST_COUNT?.[1]
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : 0,
          });
        }
      } else {
        setAtRiskCustomerFrequency({
          chartData: [],
          risk_1: "",
          risk_2: "",
          stable: "",
        });
        setAtRiskRevenueFrequency({
          chartData: [],
          risk_1: "",
          risk_2: "",
          stable: "",
        });
      }
    }
    if (wsr12RawData) {
      const wsrRawDataFilter = GetObject(
        GetArray(wsr12RawData).filter(
          (item: any) => item.BRANCH === selectedBranch.value
        )
      );

      if (Object.keys(wsrRawDataFilter)?.length > 0) {
        const tempGrowthForecastSurveyChartData: any[] = [];
        const tempGrowthForecastNetPromoterData: any[] = [];
        if (
          wsrRawDataFilter?.wsr_date &&
          wsrRawDataFilter?.wsr_date?.length ===
            wsrRawDataFilter?.neg_percent?.length &&
          wsrRawDataFilter?.wsr_date?.length ===
            wsrRawDataFilter?.neutral_percent?.length &&
          wsrRawDataFilter?.wsr_date?.length ===
            wsrRawDataFilter?.pos_percent?.length &&
          wsrRawDataFilter?.wsr_date?.length ===
            wsrRawDataFilter?.wsr_target?.length &&
          wsrRawDataFilter?.wsr_date?.length === wsrRawDataFilter?.wsr?.length
        ) {
          wsrRawDataFilter?.wsr_date.forEach((element: any, index: any) => {
            tempGrowthForecastSurveyChartData.push({
              name: element,
              "0 - 6": wsrRawDataFilter?.neg_percent?.[index],
              "7 & 8": wsrRawDataFilter?.neutral_percent?.[index],
              "9 & 10": wsrRawDataFilter?.pos_percent?.[index],
            });
            tempGrowthForecastNetPromoterData.push({
              name: element,
              "NPS GOAL": wsrRawDataFilter?.wsr_target?.[index],
              NPS: wsrRawDataFilter?.wsr?.[index],
            });
          });
        }

        setGrowthForecastSurveyChartData(tempGrowthForecastSurveyChartData);
        setGrowthForecastNetPromoterData(tempGrowthForecastNetPromoterData);

        setGrowthForecastSurveyNegPercent(
          wsrRawDataFilter?.neg_percent
            ? wsrRawDataFilter?.neg_percent?.[
                wsrRawDataFilter?.neg_percent?.length - 1
              ]
            : ""
        );
        setGrowthForecastSurveyNeuPercent(
          wsrRawDataFilter?.neutral_percent
            ? wsrRawDataFilter?.neutral_percent?.[
                wsrRawDataFilter?.neg_percent?.length - 1
              ]
            : ""
        );
        setGrowthForecastSurveyPosPercent(
          wsrRawDataFilter?.pos_percent
            ? wsrRawDataFilter?.pos_percent?.[
                wsrRawDataFilter?.pos_percent?.length - 1
              ]
            : ""
        );
        setGrowthForecastSurveyCurrentDate(
          wsrRawDataFilter?.wsr_date
            ? wsrRawDataFilter?.wsr_date?.[wsrRawDataFilter?.wsr_date?.length - 1]
            : []
        );
      } else {
        setGrowthForecastSurveyChartData([]);
        setGrowthForecastNetPromoterData([]);
      }
    }

    if (wsr36RawData) {
      const wsr36Filter = GetObject(
        GetArray(wsr36RawData).filter(
          (item: any) => item.BRANCH === selectedBranch.value
          // &&
          // item.PRODUCT_TYPE_ALL === selectedDepartment.value
        )
      );

      setGrowthForecastNetPromoter12m(wsr36Filter?.wsr_12m?.at(-1));
      setGrowthForecastNetPromoter6m(wsr36Filter?.wsr_6m?.at(-1));
      setGrowthForecastNetPromoter3m(wsr36Filter?.wsr_3m?.at(-1));
    }

    if (gainLost6RawData) {
      const gl6Filter = GetObject(
        GetArray(gainLost6RawData).filter(
          (item: any) =>
            item.BRANCH === selectedBranch.value &&
            item.PRODUCT_TYPE_ALL === selectedDepartment.value
        )
      );

      if (Object.keys(gl6Filter)?.length > 0) {
        setsalesMarketingGainLost6ChartGainAcc(
          WithCommas(gl6Filter.GAINED_ACCOUNT ? gl6Filter.GAINED_ACCOUNT : "-")
        );
        setsalesMarketingGainLost6ChartLostAcc(
          WithCommas(gl6Filter.LOST_ACCOUNT ? gl6Filter.LOST_ACCOUNT : "-")
        );
      } else {
        setsalesMarketingGainLost6ChartGainAcc([]);
        setsalesMarketingGainLost6ChartLostAcc([]);
      }
    }

    if (gainLost12RawData) {
      const gl12Filter = GetObject(
        GetArray(gainLost12RawData).filter(
          (item: any) =>
            item.BRANCH === selectedBranch.value &&
            item.PRODUCT_TYPE_ALL === selectedDepartment.value
        )
      );

      if (Object.keys(gl12Filter)?.length > 0) {
        setsalesMarketingGainLost12ChartGainAcc(
          WithCommas(
            gl12Filter.GAINED_ACCOUNT ? gl12Filter.GAINED_ACCOUNT : "-"
          )
        );
        setsalesMarketingGainLost12ChartLostAcc(
          WithCommas(gl12Filter.LOST_ACCOUNT ? gl12Filter.LOST_ACCOUNT : "-")
        );
      } else {
        setsalesMarketingGainLost12ChartGainAcc([]);
        setsalesMarketingGainLost12ChartLostAcc([]);
      }
    }

    if (gainLost36RawData) {
      const gl36Filter = GetObject(
        GetArray(gainLost36RawData).filter(
          (item: any) =>
            item.BRANCH === selectedBranch.value &&
            item.PRODUCT_TYPE_ALL === selectedDepartment.value
        )
      );

      if (Object.keys(gl36Filter)?.length > 0) {
        setsalesMarketingGainLost36ChartGainAcc(
          WithCommas(
            gl36Filter.GAINED_ACCOUNT ? gl36Filter.GAINED_ACCOUNT : "-"
          )
        );
        setsalesMarketingGainLost36ChartLostAcc(
          WithCommas(gl36Filter.LOST_ACCOUNT ? gl36Filter.LOST_ACCOUNT : "-")
        );
      } else {
        setsalesMarketingGainLost36ChartGainAcc([]);
        setsalesMarketingGainLost36ChartLostAcc([]);
      }
    }

  };

  useEffect(() => {
    growthForecastFilterData();
  }, [
    forecastData,
    revinvData,
    selectedBranch,
    selectedDepartment,
    atRiskData,
    benchRawData,
    dealerRawData,
    benchWSGRawData,
    benchBranches,
    wsr12RawData,
    surveyRawData,
    activeForecast,
    monthlyView,
    wsr36RawData,
    selectedDealerBenchmarkOption,
    allRevinvData,
    activeGrowthRate,
  ]);

  const rolingGrowthRateFormater = (event: any, index: any, key: any) => {
    if (key === "ACCOUNT_GROWTH") {
      return `${event}% ${WithCommas(
        growthForecastChartGrowthRateData[index?.dataIndex]?.TOTAL_ACCOUNT
      )}`;
    } else if (key === "INVOICE_GROWTH") {
      return `${event}% ${WithCommas(
        growthForecastChartGrowthRateData[index?.dataIndex]?.INVOICE_COUNT
      )}`;
    } else if (key === "REVENUE_GROWTH") {
      return `${event}% $${WithCommas(
        growthForecastChartGrowthRateData[index?.dataIndex]?.TOTAL_REVENUE
      )}`;
    } else if (key === "ALL_ACCOUNT_GROWTH") {
      return `${event}% ${WithCommas(
        growthForecastChartGrowthRateData36[index?.dataIndex]?.ALL_TOTAL_ACCOUNT
      )}`;
    } else if (key === "ALL_INVOICE_GROWTH") {
      return `${event}% ${WithCommas(
        growthForecastChartGrowthRateData36[index?.dataIndex]?.ALL_INVOICE_COUNT
      )}`;
    } else if (key === "ALL_REVENUE_GROWTH") {
      return `${event}% $${WithCommas(
        growthForecastChartGrowthRateData36[index?.dataIndex]?.ALL_TOTAL_REVENUE
      )}`;
    } else {
      return event;
    }
  };

  const tickFormatter = (value: string) => {
    const details = value.split(" ");
    let info = "";
    if (details.length > 2) {
      details.forEach((element: any) => {
        info += `${element}\n`;
      });
      return info;
    } else return value;
  };

  return (
    <Grid container sx={{}}>
      <Grid
        container
        sx={{
          padding: "0px 20px 10px 20px",
        }}
      >
        <Grid container sx={{}}>
          <Grid container item xs={6} sx={{ padding: "10px" }}>
            <SectionCard
              title="Rolling 12 Months Growth Rate"
              action={
                <MoreVertIcon
                  sx={{ color: tokens.cardChrome.headerText }}
                  onClick={() => handleGrowthRateNav("36")}
                />
              }
            >
              {getGrowthRateDetailLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{
                    padding: "10px 20px 10px 20px",
                    border: tokens.cardChrome.bodyBorder,
                    backgroundColor: tokens.cardChrome.bodyBg,
                    gap: 2,
                  }}
                >
                  <Grid container item xs={12} sx={{}}>
                    <Grid container item xs={3} sx={{ padding: "5px" }}>
                      <StatTile
                        size="lg"
                        value={growthForecastActiveInvoice}
                        label="Transactions"
                        delta={Number(growthForecastInvoiceGrowth)}
                      />
                    </Grid>
                    <Grid container item xs={3} sx={{ padding: "5px" }}>
                      <StatTile
                        size="lg"
                        value={growthForecastActiveAccount}
                        label="Accounts"
                        delta={Number(growthForecastAccountGrowth)}
                      />
                    </Grid>
                    <Grid container item xs={6} sx={{ padding: "5px" }}>
                      <StatTile
                        size="lg"
                        value={<>$ {growthForecastActiveRevenue}</>}
                        label="Revenue"
                        delta={Number(growthForecastRevenueGrowth)}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sx={{ display: "flex" }}>
                    <SegmentedControl
                      options={[
                        { value: "36", label: "36 Months" },
                        { value: "12", label: "12 Month" },
                      ]}
                      value={activeGrowthRate}
                      onChange={handleGrowthRateNav}
                    />
                  </Grid>
                  <Grid container item xs={12} sx={{ height: "350px" }}>
                    {activeGrowthRate === "12" ? (
                      <LineChart
                        sx={{
                          [`& .${markElementClasses.root}`]: {
                            scale: "0.6",
                            strokeWidth: 2,
                          },
                        }}
                        series={Object.keys(keyToLabel).map((key) => {
                          return {
                            dataKey: key,
                            label: keyToLabel[key],
                            color: colors[key],
                            // showMark: false,
                            valueFormatter: (event, index) =>
                              rolingGrowthRateFormater(event, index, key),
                          };
                        })}
                        dataset={growthForecastChartGrowthRateData}
                        xAxis={[
                          {
                            dataKey: "period",
                            scaleType: "point",
                            tickPlacement: "middle",
                            tickLabelStyle: {
                              angle: -30,
                              textAnchor: "end",
                              fontSize: 12,
                            },
                          },
                        ]}
                        yAxis={[
                          {
                            label: "Percentage %",
                            scaleType: "linear",
                          },
                        ]}
                        slotProps={{
                          legend: {
                            direction: "row",
                            position: { vertical: "top", horizontal: "right" },
                            padding: 0,
                            itemMarkWidth: 30,
                            itemMarkHeight: 10,
                          },
                        }}
                      />
                    ) : (
                      <LineChart
                        sx={{
                          [`& .${markElementClasses.root}`]: {
                            scale: "0.6",
                            strokeWidth: 2,
                          },
                        }}
                        series={Object.keys(keyToLabel36).map((key) => {
                          return {
                            dataKey: key,
                            label: keyToLabel36[key],
                            color: colors[key],
                            valueFormatter: (event, index) =>
                              rolingGrowthRateFormater(event, index, key),
                          };
                        })}
                        dataset={growthForecastChartGrowthRateData36}
                        xAxis={[
                          {
                            dataKey: "period",
                            scaleType: "point",
                            tickPlacement: "middle",
                            tickLabelStyle: {
                              angle: -30,
                              textAnchor: "end",
                              fontSize: 12,
                            },
                          },
                        ]}
                        yAxis={[
                          {
                            label: "Percentage %",
                            scaleType: "linear",
                          },
                        ]}
                        slotProps={{
                          legend: {
                            direction: "row",
                            position: { vertical: "top", horizontal: "right" },
                            padding: 0,
                            itemMarkWidth: 30,
                            itemMarkHeight: 10,
                          },
                        }}
                        slots={{}}
                      />
                    )}
                  </Grid>
                </Grid>
              )}
            </SectionCard>
          </Grid>
          <Grid container item xs={6} sx={{ padding: "10px" }}>
            <SectionCard title="Rolling 12 Months Forecast">
              {forecastLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{
                    padding: "10px 20px 10px 20px",
                    border: tokens.cardChrome.bodyBorder,
                    backgroundColor: tokens.cardChrome.bodyBg,
                    gap: 2,
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <SegmentedControl
                      options={[
                        { value: "ORDER_FREQUENCY", label: "ORDER FREQUENCY" },
                        { value: "ACTIVE_ACCOUNT", label: "ACTIVE ACCOUNT" },
                        { value: "REVENUE", label: "REVENUE" },
                      ]}
                      value={activeForecast}
                      onChange={handleForecastNav}
                    />
                  </Grid>
                  <Grid container item xs={12} sx={{}}>
                    <Grid container item xs={5} sx={{ padding: "5px" }}>
                      <StatTile
                        size="lg"
                        align="left"
                        value={growthForecastForecastActual}
                        label="Current"
                        delta={Number(growthForecastForecastActualGrowth)}
                      />
                    </Grid>
                    <Grid container item xs={5} sx={{ padding: "5px" }}>
                      <StatTile
                        size="lg"
                        align="left"
                        value={growthForecastForecastForecast}
                        label="Projected"
                        delta={
                          isNumeric(growthForecastForecastGrowth)
                            ? Number(growthForecastForecastGrowth)
                            : null
                        }
                      />
                    </Grid>
                    <Grid container item xs={2} sx={{ padding: "5px" }}>
                      <StatTile size="sm" value={<>&gt; 95%</>} label="Confidence" />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} sx={{}}>
                    <SegmentedControl
                      options={[
                        { value: "rolling", label: "12 Months Rolling View" },
                        { value: "monthly", label: "Monthly View" },
                      ]}
                      value={monthlyView ? "monthly" : "rolling"}
                      onChange={(v) => setMonthlyView(v === "monthly")}
                    />
                  </Grid>
                  <Grid container item xs={12} sx={{ height: "300px" }}>
                    {growthForecastForecastChartData?.length > 0 && (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={growthForecastForecastChartData}
                          margin={{
                            top: 10,
                            right: 20,
                            bottom: 60,
                            left: 20,
                          }}
                        >
                          <XAxis
                            dataKey="name"
                            scale="auto"
                            angle={-80}
                            tickMargin={25}
                            style={{
                              fontSize: "12px",
                            }}
                            interval={0}
                          />
                          <YAxis
                            tickFormatter={(value) => {
                              if (activeForecast === "REVENUE") {
                                return `$ ${new Intl.NumberFormat("en-US", {
                                  notation: "compact",
                                  compactDisplay: "short",
                                }).format(value)}`;
                              } else {
                                return value;
                              }
                            }}
                          />
                          <Tooltip
                            content={
                              activeForecast === "REVENUE" ? (
                                <CustomTooltipRevenue />
                              ) : (
                                <CustomTooltip />
                              )
                            }
                          />
                          <Legend verticalAlign="top" />
                          <Bar
                            name="Actual"
                            dataKey="actual"
                            fill={tokens.chart.forecast.actual}
                          />
                          <Line
                            name="Lower"
                            dataKey="lower"
                            stroke={tokens.chart.forecast.bounds}
                            activeDot={{ r: 0.5 }}
                          />
                          <Line
                            name="Forecast"
                            dataKey="forecast"
                            stroke={tokens.chart.forecast.line}
                            activeDot={{ r: 0.5 }}
                          />
                          <Line
                            name="Upper"
                            dataKey="upper"
                            stroke={tokens.chart.forecast.bounds}
                            activeDot={{ r: 5 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </Grid>
                </Grid>
              )}
            </SectionCard>
          </Grid>
        </Grid>
        <Grid container sx={{}}>
          <Grid container item xs={6} sx={{ padding: "10px" }}>
            <SectionCard title="Dealer Benchmark Metric Rolling 12 Months">
              {emailReportsLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{
                    padding: "10px 20px 10px 20px",
                    border: tokens.cardChrome.bodyBorder,
                    backgroundColor: tokens.cardChrome.bodyBg,
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Grid
                    container
                    item
                    xs={12}
                    sx={{ padding: "0px 10% 0px 10%" }}
                  >
                    <Grid
                      item
                      xs={6}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <FormControl fullWidth>
                        <Autocomplete
                          id="program"
                          value={selectedDealerBenchmarkOption}
                          onChange={(
                            _: SyntheticEvent,
                            newValue: string | null
                          ) => handleDealerBenchmarkChange(newValue)}
                          options={growthForecastDealerBenchmarkOption}
                          isOptionEqualToValue={(option, newValue) => {
                            return option.value === newValue.value;
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Dealer Type"
                              placeholder="Dealer Type"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} sx={{ height: "400px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dealerBenchChartData}
                        margin={{
                          top: 20,
                          right: 0,
                          left: 0,
                          bottom: 120,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          angle={-80}
                          tickMargin={70}
                          tickFormatter={tickFormatter}
                        />
                        <YAxis
                          label={{
                            value: "Percentage %",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip content={<CustomTooltipPercentage />} />
                        <Legend verticalAlign="top" />
                        <Bar
                          dataKey="you"
                          fill={tokens.chart.benchmark.you}
                          label={{ position: "top" }}
                        />
                        <Bar
                          dataKey="average"
                          fill={tokens.chart.benchmark.average}
                          label={{ position: "top" }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              )}
            </SectionCard>
          </Grid>
          <Grid container item xs={6} sx={{ padding: "10px" }}>
            <SectionCard title="At Risk Customer Distribution">
              {atRiskLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{
                    padding: "5px 5px 5px 5px",
                    border: tokens.cardChrome.bodyBorder,
                    backgroundColor: tokens.cardChrome.bodyBg,
                    gap: 2,
                  }}
                >
                  <Grid container item xs={12} sx={{}}>
                    <Grid container item xs={4} sx={{ padding: "10px" }}>
                      <Grid
                        container
                        item
                        xs={12}
                        sx={{
                          backgroundColor: tokens.chart.risk.stableBg,
                          padding: "10px",
                          borderRadius: "5px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: "bold",
                            color: tokens.chart.risk.stable,
                          }}
                        >
                          STABLE
                        </Typography>
                        <Typography
                          sx={{ color: tokens.tile.eyebrow, fontWeight: "bold" }}
                        >
                          &lt; 5%
                        </Typography>
                        <Typography>Chance of losing them</Typography>
                        <Typography>
                          $ {growthForecastRevPerCustStable?.REV_PER_CUST} Avg
                          Rev
                        </Typography>
                        <Typography>
                          {growthForecastRevPerCustStable?.CUST_COUNT} Customers
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container item xs={4} sx={{ padding: "10px" }}>
                      <Grid
                        container
                        item
                        xs={12}
                        sx={{
                          backgroundColor: tokens.chart.risk.risk1Bg,
                          padding: "10px",
                          borderRadius: "5px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: "bold",
                            color: tokens.chart.risk.risk1,
                          }}
                        >
                          Risk1:
                        </Typography>
                        <Typography
                          sx={{ color: tokens.tile.eyebrow, fontWeight: "bold" }}
                        >
                          5% - 50%
                        </Typography>
                        <Typography>Chance of losing them</Typography>
                        <Typography>
                          $ {growthForecastRevPerCustAtrist1?.REV_PER_CUST} Avg
                          Rev
                        </Typography>
                        <Typography>
                          {growthForecastRevPerCustAtrist1?.CUST_COUNT}{" "}
                          Customers
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container item xs={4} sx={{ padding: "10px" }}>
                      <Grid
                        container
                        item
                        xs={12}
                        sx={{
                          backgroundColor: tokens.chart.risk.risk2Bg,
                          padding: "10px",
                          borderRadius: "5px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: "bold",
                            color: tokens.chart.risk.risk2,
                          }}
                        >
                          Risk2:
                        </Typography>
                        <Typography
                          sx={{ color: tokens.tile.eyebrow, fontWeight: "bold" }}
                        >
                          &gt; 50%
                        </Typography>
                        <Typography>Chance of losing them</Typography>
                        <Typography>
                          $ {growthForecastRevPerCustAtrist2?.REV_PER_CUST} Avg
                          Rev
                        </Typography>
                        <Typography>
                          {growthForecastRevPerCustAtrist2?.CUST_COUNT}{" "}
                          Customers
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} sx={{}}>
                    <Grid container item xs={4} sx={{ padding: "10px" }}>
                      <Grid
                        container
                        item
                        xs={7}
                        sx={{ backgroundColor: tokens.tile.piePanelBg }}
                      >
                        <PieChart
                          series={[
                            {
                              data: atRiskCustomerFrequency.chartData ?? [],
                              innerRadius: 40,
                              outerRadius: 50,
                            },
                          ]}
                          height={200}
                          slotProps={{
                            legend: {
                              hidden: true,
                            },
                          }}
                          margin={{
                            bottom: 0,
                            left: 0,
                            right: 0,
                            top: 0,
                          }}
                          sx={{
                            borderRadius: "10px 0px 0px 10px",
                          }}
                        >
                          <PieCenterLabel>CUSTOMER #</PieCenterLabel>
                        </PieChart>
                      </Grid>
                      <Grid
                        container
                        item
                        xs={5}
                        sx={{
                          backgroundColor: tokens.tile.piePanelBg,
                          borderRadius: "0px 10px 10px 0px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: tokens.chart.risk.risk1,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          RISK 1: {atRiskCustomerFrequency.risk_1}%
                        </Typography>
                        <Typography
                          sx={{
                            color: tokens.chart.risk.risk2,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          RISK 2: {atRiskCustomerFrequency.risk_2}%
                        </Typography>
                        <Typography
                          sx={{
                            color: tokens.chart.risk.stable,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          STABLE: {atRiskCustomerFrequency.stable}%
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container item xs={4} sx={{ padding: "10px" }}>
                      <Grid
                        container
                        item
                        xs={7}
                        sx={{ backgroundColor: tokens.tile.piePanelBg }}
                      >
                        <PieChart
                          series={[
                            {
                              data: atRiskTransactionFrequency.chartData ?? [],
                              innerRadius: 40,
                              outerRadius: 50,
                            },
                          ]}
                          height={200}
                          slotProps={{
                            legend: {
                              hidden: true,
                            },
                          }}
                          margin={{
                            bottom: 0,
                            left: 0,
                            right: 0,
                            top: 0,
                          }}
                          sx={{
                            borderRadius: "10px 0px 0px 10px",
                          }}
                        >
                          <PieCenterLabel>TRANSACTION #</PieCenterLabel>
                        </PieChart>
                      </Grid>
                      <Grid
                        container
                        item
                        xs={5}
                        sx={{
                          backgroundColor: tokens.tile.piePanelBg,
                          borderRadius: "0px 10px 10px 0px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: tokens.chart.risk.risk1,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          RISK 1: {atRiskTransactionFrequency.risk_1}%
                        </Typography>
                        <Typography
                          sx={{
                            color: tokens.chart.risk.risk2,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          RISK 2: {atRiskTransactionFrequency.risk_2}%
                        </Typography>
                        <Typography
                          sx={{
                            color: tokens.chart.risk.stable,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          STABLE: {atRiskTransactionFrequency.stable}%
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={4}
                      sx={{ padding: "10px", borderRadius: "10px" }}
                    >
                      <Grid
                        container
                        item
                        xs={7}
                        sx={{ backgroundColor: tokens.tile.piePanelBg }}
                      >
                        <PieChart
                          series={[
                            {
                              data: atRiskRevenueFrequency.chartData ?? [],
                              innerRadius: 40,
                              outerRadius: 50,
                              valueFormatter: (v: any) => {
                                if (v) {
                                  return `$ ${WithCommas(v.data)}`;
                                } else return "";
                              },
                            },
                          ]}
                          height={200}
                          slotProps={{
                            legend: {
                              hidden: true,
                            },
                          }}
                          margin={{
                            bottom: 0,
                            left: 0,
                            right: 0,
                            top: 0,
                          }}
                          sx={{
                            borderRadius: "10px 0px 0px 10px",
                          }}
                        >
                          <PieCenterLabel>REVENUE $</PieCenterLabel>
                        </PieChart>
                      </Grid>
                      <Grid
                        container
                        item
                        xs={5}
                        sx={{
                          backgroundColor: tokens.tile.piePanelBg,
                          borderRadius: "0px 10px 10px 0px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: tokens.chart.risk.risk1,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          RISK 1: {atRiskRevenueFrequency.risk_1}%
                        </Typography>
                        <Typography
                          sx={{
                            color: tokens.chart.risk.risk2,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          RISK 2: {atRiskRevenueFrequency.risk_2}%
                        </Typography>
                        <Typography
                          sx={{
                            color: tokens.chart.risk.stable,
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          STABLE: {atRiskRevenueFrequency.stable}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </SectionCard>
          </Grid>
        </Grid>
        <Grid container sx={{}}>
          <Grid container item xs={6} sx={{ padding: "10px" }}>
            <SectionCard title="Net Promoter Score">
              {customerSurveyLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{
                    padding: "10px 20px 10px 20px",
                    border: tokens.cardChrome.bodyBorder,
                    backgroundColor: tokens.cardChrome.bodyBg,
                    gap: 2,
                  }}
                >
                  <Grid container item xs={12} sx={{}}>
                    <Grid container item xs={5} sx={{ padding: "5px" }}>
                      {activeNetPromoter === "SURVEY_RESULT" ? (
                        <Grid
                          container
                          item
                          xs={12}
                          sx={{
                            padding: "10px",
                            borderRadius: "5px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              color: tokens.tile.eyebrow,
                              fontWeight: "bold",
                            }}
                          >
                            AS OF TODAY
                          </Typography>
                          <Typography sx={{ color: tokens.tile.eyebrow }}>
                            GOAL: &gt; 75%
                          </Typography>
                          <Typography
                            sx={{
                              color: tokens.chart.gainLost.gained,
                              fontWeight: "bold",
                            }}
                          >
                            RATED 9 & 10
                          </Typography>
                        </Grid>
                      ) : (
                        <Grid
                          container
                          item
                          xs={12}
                          sx={{
                            padding: "10px",
                            borderRadius: "5px",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              color: tokens.tile.eyebrow,
                              fontWeight: "bold",
                            }}
                          >
                            NPS GOAL:
                          </Typography>
                          <Typography
                            sx={{
                              color: tokens.chart.delta.down,
                              fontWeight: "bold",
                            }}
                          >
                            70%
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    <Grid
                      container
                      item
                      xs={7}
                      sx={{ justifyContent: "flex-end" }}
                    >
                      <Grid
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Grid
                          sx={{
                            backgroundColor: tokens.color.primary,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "50px",
                            borderRadius: "5px 5px 0px 0px",
                            border: tokens.cardChrome.bodyBorder,
                          }}
                        >
                          <Typography
                            sx={{ color: tokens.cardChrome.headerText, fontWeight: "bold" }}
                          >
                            {growthForecastSurveyCurrentDate.replace("-", " ")}
                          </Typography>
                        </Grid>
                        <Grid
                          container
                          item
                          xs={12}
                          sx={{
                            padding: "10px",
                            border: tokens.cardChrome.bodyBorder,
                            backgroundColor: tokens.tile.stripBg,
                          }}
                        >
                          <Grid container item xs={4} sx={{ padding: "5px" }}>
                            <StatTile
                              orientation="row"
                              size="xs"
                              bg={tokens.cardChrome.bodyBg}
                              label={
                                activeNetPromoter === "SURVEY_RESULT"
                                  ? "0 - 6 :"
                                  : "3 Month:"
                              }
                              value={
                                activeNetPromoter === "SURVEY_RESULT"
                                  ? `${growthForecastSurveyNegPercent}%`
                                  : growthForecastNetPromoter3m
                              }
                              valueColor={tokens.chart.delta.down}
                            />
                          </Grid>
                          <Grid container item xs={4} sx={{ padding: "5px" }}>
                            <StatTile
                              orientation="row"
                              size="xs"
                              bg={tokens.cardChrome.bodyBg}
                              label={
                                activeNetPromoter === "SURVEY_RESULT"
                                  ? "7 & 8 :"
                                  : "6 Month:"
                              }
                              value={
                                activeNetPromoter === "SURVEY_RESULT"
                                  ? `${growthForecastSurveyNeuPercent}%`
                                  : growthForecastNetPromoter6m
                              }
                              valueColor={tokens.chart.risk.risk1}
                            />
                          </Grid>
                          <Grid container item xs={4} sx={{ padding: "5px" }}>
                            <StatTile
                              orientation="row"
                              size="xs"
                              bg={tokens.cardChrome.bodyBg}
                              label={
                                activeNetPromoter === "SURVEY_RESULT"
                                  ? "9 & 10 :"
                                  : "12 Month:"
                              }
                              value={
                                activeNetPromoter === "SURVEY_RESULT"
                                  ? `${growthForecastSurveyPosPercent}%`
                                  : growthForecastNetPromoter12m
                              }
                              valueColor={tokens.chart.delta.up}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <SegmentedControl
                      options={[
                        { value: "SURVEY_RESULT", label: "SURVEY RESULT" },
                        { value: "NPS_SCORE", label: "NPS SCORE" },
                      ]}
                      value={activeNetPromoter}
                      onChange={handleNetPromoterNav}
                    />
                  </Grid>
                  <Grid container item xs={12} sx={{ height: "300px" }}>
                    {activeNetPromoter === "SURVEY_RESULT" ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          width={500}
                          height={300}
                          data={growthForecastSurveyChartData}
                          margin={{
                            top: 10,
                            right: 0,
                            left: 0,
                            bottom: 40,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            scale="auto"
                            angle={-80}
                            tickMargin={25}
                            style={{
                              fontSize: "12px",
                            }}
                            interval={0}
                          />
                          <YAxis />
                          <Tooltip content={<CustomTooltipPercentage />} />
                          <Legend
                            verticalAlign="top"
                            style={{ padding: "10px" }}
                          />
                          <Bar
                            dataKey="0 - 6"
                            stackId="a"
                            fill={tokens.chart.nps.detractors}
                          />
                          <Bar
                            dataKey="7 & 8"
                            stackId="a"
                            fill={tokens.chart.nps.passives}
                          />
                          <Bar
                            dataKey="9 & 10"
                            stackId="a"
                            fill={tokens.chart.nps.promoters}
                          />
                          <Tooltip content={<CustomTooltipPercentage />} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={growthForecastNetPromoterData}
                          margin={{
                            top: 20,
                            right: 0,
                            bottom: 50,
                            left: 0,
                          }}
                        >
                          <CartesianGrid stroke={tokens.chart.gridline} />
                          <XAxis
                            dataKey="name"
                            scale="auto"
                            angle={-80}
                            tickMargin={25}
                            style={{
                              fontSize: "12px",
                            }}
                            interval={0}
                          />
                          <YAxis />
                          <Tooltip content={<CustomTooltipPercentage />} />
                          <Legend
                            verticalAlign="top"
                            align="right"
                            wrapperStyle={{
                              paddingBottom: "20px",
                            }}
                          />
                          <Bar
                            dataKey="NPS"
                            barSize={30}
                            fill={tokens.chart.series.activeAccount}
                            label={{ position: "top" }}
                          />
                          <Line
                            type="monotone"
                            dataKey="NPS GOAL"
                            stroke={tokens.chart.goalLine}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </Grid>
                </Grid>
              )}
            </SectionCard>
          </Grid>
          <Grid container item xs={6} sx={{ padding: "10px" }}>
            <SectionCard title={activeRetentionGainLostTitle}>
              {retentionRateLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{
                    padding: "10px 20px 10px 20px",
                    border: tokens.cardChrome.bodyBorder,
                    backgroundColor: tokens.cardChrome.bodyBg,
                    gap: 2,
                  }}
                >
                  <Grid container sx={{ justifyContent: "flex-end" }}>
                    <Grid>
                      <SegmentedControl
                        size="sm"
                        options={[
                          { value: "RETENTION", label: "Retention" },
                          { value: "GAIN_LOST_ACCOUNT", label: "GAIN & LOST" },
                          { value: "ACTIVE_ACCOUNT", label: "Active Account" },
                        ]}
                        value={activeRetentionGainLost}
                        onChange={(v) =>
                          handleRetentionGainLost(
                            v,
                            v === "RETENTION"
                              ? "Retention Rate"
                              : v === "GAIN_LOST_ACCOUNT"
                              ? "Gain & Lost Account"
                              : "Active Account"
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  {activeRetentionGainLost === "RETENTION" && (
                    <Grid container item xs={12} sx={{}}>
                      <Grid container>
                        <Grid container item xs={8} sx={{ padding: "5px" }}>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              padding: "10px",
                              borderRadius: "5px",
                            }}
                          >
                            <Typography
                              sx={{ color: tokens.tile.caption }}
                            >
                              Retention drives growth and frequency of purchase.
                            </Typography>
                            <Typography
                              sx={{ color: tokens.tile.caption }}
                            >
                              Retention scores &gt; 96% drive growth by &gt; 10%
                              per year
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid container item xs={4} sx={{ padding: "5px" }}>
                          <Grid
                            container
                            item
                            xs={12}
                            sx={{
                              backgroundColor: "",
                              borderRadius: "5px",
                              border: tokens.cardChrome.bodyBorder,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color: tokens.tile.eyebrow,
                                fontWeight: "bold",
                              }}
                            >
                              RETENTION RATE:
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                color: tokens.chart.series.activeAccount,
                                fontWeight: "bold",
                              }}
                            >
                              {growthForecastLastRetentionRate} %
                            </Typography>
                            <Typography
                              sx={{ color: tokens.tile.caption }}
                            >
                              CURRENTLY 12 ROLLING
                            </Typography>
                            <Typography
                              sx={{ color: tokens.tile.caption }}
                            >
                              RETENTION RATE
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid container item xs={12} sx={{ height: "350px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            data={growthForecastRetentionData}
                            margin={{
                              top: 20,
                              right: 20,
                              bottom: 50,
                              left: 0,
                            }}
                          >
                            <CartesianGrid stroke={tokens.chart.gridline} />
                            <XAxis
                              dataKey="name"
                              scale="auto"
                              angle={-80}
                              tickMargin={25}
                              style={{
                                fontSize: "12px",
                              }}
                              interval={0}
                            />
                            <YAxis />
                            <Tooltip content={<CustomTooltipPercentage />} />
                            <Legend
                              verticalAlign="top"
                              align="right"
                              wrapperStyle={{
                                paddingBottom: "20px",
                              }}
                            />
                            <Bar
                              dataKey="Retention Rate"
                              barSize={30}
                              fill={tokens.chart.series.activeAccount}
                              label={{ position: "top" }}
                            />
                            <Line
                              type="monotone"
                              dataKey="Retention Goal"
                              stroke={tokens.chart.goalLine}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  )}
                  {activeRetentionGainLost === "GAIN_LOST_ACCOUNT" && (
                    <Grid container>
                      <Grid
                        container
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <Grid
                          container
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Grid
                            container
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Grid item xs={2}>
                              <Typography
                                sx={{
                                  fontSize: "25px",
                                }}
                              >
                                <span style={{ fontWeight: "bold" }}>
                                  GAINED:
                                </span>{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                sx={{
                                  fontSize: "20px",
                                }}
                              >
                                <span style={{ fontWeight: "bold" }}>
                                  6 MONTH:
                                </span>{" "}
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: tokens.chart.gainLost.gained,
                                  }}
                                >
                                  {salesMarketingGainLost6ChartGainAcc}
                                </span>{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                sx={{ fontSize: "20px", alignItems: "end" }}
                              >
                                <span style={{ fontWeight: "bold" }}>
                                  12 MONTH:
                                </span>{" "}
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: tokens.chart.gainLost.gained,
                                  }}
                                >
                                  {salesMarketingGainLost12ChartGainAcc}
                                </span>
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                sx={{ fontSize: "20px", alignItems: "end" }}
                              >
                                {" "}
                                <span style={{ fontWeight: "bold" }}>
                                  36 MONTH:
                                </span>{" "}
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: tokens.chart.gainLost.gained,
                                  }}
                                >
                                  {salesMarketingGainLost36ChartGainAcc}
                                </span>
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid
                            container
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Grid item xs={2}>
                              <Typography
                                sx={{
                                  fontSize: "25px",
                                }}
                              >
                                <span style={{ fontWeight: "bold" }}>
                                  LOST:
                                </span>{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                sx={{
                                  fontSize: "20px",
                                }}
                              >
                                <span style={{ fontWeight: "bold" }}>
                                  6 MONTH:
                                </span>{" "}
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: tokens.chart.gainLost.lost,
                                  }}
                                >
                                  {salesMarketingGainLost6ChartLostAcc}
                                </span>
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                sx={{
                                  fontSize: "20px",
                                }}
                              >
                                <span style={{ fontWeight: "bold" }}>
                                  12 MONTH:
                                </span>{" "}
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: tokens.chart.gainLost.lost,
                                  }}
                                >
                                  {salesMarketingGainLost12ChartLostAcc}
                                </span>
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                sx={{
                                  fontSize: "20px",
                                }}
                              >
                                <span style={{ fontWeight: "bold" }}>
                                  36 MONTH:
                                </span>{" "}
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: tokens.chart.gainLost.lost,
                                  }}
                                >
                                  {salesMarketingGainLost36ChartLostAcc}
                                </span>
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid container item xs={12} sx={{ height: "300px" }}>
                        <LineChart
                          series={[
                            {
                              data: salesMarketingGainLostChartGainAcc,
                              label: "Gained",
                              color: tokens.chart.gainLost.gained,
                            },
                            {
                              data: salesMarketingGainLostChartLostAcc,
                              label: "Lost",
                              color: tokens.chart.gainLost.lost,
                            },
                          ]}
                          xAxis={[
                            {
                              scaleType: "point",
                              data: salesMarketingActiveAccountChartLabel,
                              tickLabelStyle: {
                                angle: -50,
                                textAnchor: "end",
                                fontSize: 12,
                              },
                            },
                          ]}
                          slotProps={{
                            legend: {
                              direction: "row",
                              position: {
                                vertical: "top",
                                horizontal: "middle",
                              },
                              padding: 0,
                              itemMarkWidth: 30,
                              itemMarkHeight: 10,
                            },
                          }}
                          margin={{
                            left: 50,
                            right: 30,
                            top: 40,
                            bottom: 70,
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {activeRetentionGainLost === "ACTIVE_ACCOUNT" && (
                    <Grid container item xs={12} sx={{}}>
                      <Grid container item xs={12} sx={{ height: "350px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            data={growthForecastActiveAccountData}
                            margin={{
                              top: 20,
                              right: 20,
                              bottom: 50,
                              left: 0,
                            }}
                          >
                            <CartesianGrid stroke={tokens.chart.gridline} />
                            <XAxis
                              dataKey="name"
                              scale="auto"
                              angle={-80}
                              tickMargin={25}
                              style={{
                                fontSize: "12px",
                              }}
                              interval={0}
                            />
                            <YAxis yAxisId={1} />
                            <YAxis yAxisId={2} orientation="right" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                              verticalAlign="top"
                              align="right"
                              wrapperStyle={{
                                paddingBottom: "20px",
                              }}
                            />
                            <Bar
                              dataKey="Active Account"
                              barSize={30}
                              fill={tokens.chart.series.activeAccount}
                              label={{ position: "top" }}
                              yAxisId={1}
                            />
                            <Line
                              type="monotone"
                              dataKey="Account %"
                              stroke={tokens.chart.goalLine}
                              yAxisId={2}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default GrowthForecast;
